import { ResourceRef, ResourceRefOrSchema } from '../../../types/common.types'
import { UserSchema } from '../users/user.model'
import { EmployerSchema, InterimAgencySchema } from '../organizations/organization.model'
import { WorkerCounterSchema } from '../worker-counters/worker-counter.types'

export enum MissionContractModel {
  twoParties = 'twoParties', // Two distinct entities: employer and worker
  threeParties = 'threeParties', // Three distinct entities: employer, worker, and interim agency
}

/**
 * Lifecycle status for a period summary (pre-billing).
 * Used for three-parties cancellation workflow; two-parties summaries default to active.
 */
export enum PeriodSummaryStatus {
  active = 'active',
  cancellationRequested = 'cancellationRequested',
  cancelled = 'cancelled',
}

/**
 * Role of the user who initiated a cancellation request (employer member vs interim agency member).
 */
export type PeriodSummaryRequesterRole = 'employerMember' | 'interimAgencyMember'

/**
 * Embedded document for a cancellation request and its resolution (stored in current request or history).
 */
export interface PeriodSummaryCancellationRequest {
  requesterRole: PeriodSummaryRequesterRole
  requestedBy: ResourceRefOrSchema<UserSchema>
  requestedAt: Date
  requestComment: string
  decision?: 'accepted' | 'refused'
  resolvedBy?: ResourceRefOrSchema<UserSchema>
  resolvedAt?: Date
  resolutionComment?: string
}

export interface PeriodSummary {
  _id: ResourceRef
  createdAt: Date
  updatedAt: Date
  employer: ResourceRefOrSchema<EmployerSchema>
  createdBy: ResourceRefOrSchema<UserSchema>
  contractModel: MissionContractModel
  identifier: string
  invoiceRef?: string
  period: {
    type: 'week'
    start: Date
    end: Date
    year: number
    week: number
  }
  data: Partial<SummaryData>
  /** Default active; cancellation workflow updates this for three-parties pre-billings */
  status: PeriodSummaryStatus
  /** Current pending cancellation request (only when status is cancellationRequested) */
  cancellationRequest?: PeriodSummaryCancellationRequest
  /** Archive of past cancellation requests after resolution */
  cancellationHistory: PeriodSummaryCancellationRequest[]
  // Optional fields for different contract types
  interimAgency?: ResourceRefOrSchema<InterimAgencySchema> // For three-parties contracts
  worker?: ResourceRefOrSchema<UserSchema> // For two-parties contracts
}

export type SimulatedPeriodSummary = Omit<
  PeriodSummary,
  | '_id'
  | 'createdBy'
  | 'updatedBy'
  | 'createdAt'
  | 'updatedAt'
  | 'status'
  | 'cancellationHistory'
  | 'cancellationRequest'
> & {
  data: SummaryData & {
    employerData: Omit<SummaryEmployerData, 'counters'> & {
      counters: Record<string, SimulatedPeriodSummaryCounterData>
    }
  }
}

/**
 * Interface representing weekly period summary
 * Contains information about worked durations, breaks,
 * and various work time metrics over a period
 * @property durations.worked.total Total worked duration for the week (breaks subtracted)
 * @property durations.worked.day Total daytime worked duration for the week (breaks subtracted)
 * @property durations.worked.night Total nighttime worked duration for the week (breaks subtracted)
 * @property durations.worked.theoretical Theoretical duration based on daily schedules with breaks subtracted
 * @property durations.worked.abandoned Counted duration for abandoned work periods (0 when none)
 * @property durations.worked.contract.diff Difference between counted time (worked + benefit + employer rule
 *   durations when employerDataOptions.addToWorkedTotal is true) and contractual weekly duration. After employer
 *   rules are applied in period summary calculation, this is recomputed as durations.total.total minus
 *   durations.contract.total; calculateLegalDurations still sets an initial value (worked + benefit - contract)
 *   for callers that do not run employer rules.
 * @property durations.worked.contract.legal Actual legal worked duration (35h max)
 * @property durations.worked.contract.legalDiff Difference between actual worked time and legal duration
 * @property durations.worked.contract.extraLegal Actual extra-legal worked duration (e.g. 4h for a 39h contract)
 * @property durations.worked.contract.extraLegalDiff Difference between actual extra-legal worked time and extra-legal duration
 * @property durations.benefit Benefit durations (e.g. paid leave)
 * @property durations.accounted.total Final accounted duration after counter adjustments
 * @property durations.accounted.totalCounterAdjustment Sum of all counter adjustments (+ or -)
 * @property durations.theoretical Theoretical duration without breaks subtracted (on-site duration)
 * @property durations.contract.total Contractual duration to work (breaks subtracted) (legal + surplus)
 * @property durations.contract.legal Legal duration (35h max)
 * @property durations.contract.extraLegal Extra legal duration (e.g. 4h for a 39h contract)
 * @property durations.contract.remainingAfterBenefit Contractual duration remaining after subtracting benefit time
 * @property durations.diff Difference between theoretical and actual duration
 * @property durations.breaks Total break duration
 * @property durations.overtime.total Total overtime duration
 * @property durations.overtime.contractual Overtime hours for a 39h contract (35 + 4) => from 0 to 4h
 * @property durations.overtime.nonContractual Overtime hours beyond 4h contractual for a 39h contract || Total overtime for other contracts
 * @property costs.worked.total Total cost of worked time
 * @property costs.worked.day Cost of daytime worked time
 * @property costs.worked.night Cost of nighttime worked time
 * @property costs.total Total cost
 * @property costs.hourly Average hourly rate
 * @property costs.employerDataTotal Total employer costs
 * @property costs.hourlyInWorkedTime Different hourly rates applied during work periods
 * @property days Detailed summary for each weekday (0 = Monday to 6 = Sunday)
 * @property employerData Employer-specific data
 */
export interface SummaryData {
  durations: {
    worked: {
      total: number
      day: number
      night: number
      theoretical: number
      abandoned: number
      contract: {
        diff: number
        legal: number
        legalDiff: number
        extraLegal: number
        extraLegalDiff: number
      }
    }
    benefit: number
    accounted: number
    counters: {
      totalSentToCounters: number
      totalKeepInPeriod: number
      totalReceivedFromCounters: number
      totalSendToCountersFromAccountedCounters: number
    }
    total: {
      total: number
      day: number
      night: number
    }
    theoretical: number
    contract: {
      total: number
      legal: number
      extraLegal: number
      remainingAfterBenefit: number
    }
    diff: number | null
    breaks: number
    overtime: {
      total: number
      contractual: number | null
      nonContractual: number | null
    }
  }
  costs: {
    worked: {
      total: number
      day: number
      night: number
    }
    total: number
    hourly: number
    employerDataTotal: number
    hourlyInWorkedTime: HourlyCostInTime[]
  }
  days: {
    0: SummaryDayData
    1: SummaryDayData
    2: SummaryDayData
    3: SummaryDayData
    4: SummaryDayData
    5: SummaryDayData
    6: SummaryDayData
  }
  /**
   * Calendar day data (strict 00h-24h Paris time boundaries)
   * Used for employer rules with daysOfWeek restrictions
   * Key: day index (0 = Monday, 6 = Sunday, 7 = Monday of next week for overflow)
   * Day 7 captures work that starts on Sunday but ends on Monday of the following week
   */
  calendarDays: {
    0: CalendarDayData
    1: CalendarDayData
    2: CalendarDayData
    3: CalendarDayData
    4: CalendarDayData
    5: CalendarDayData
    6: CalendarDayData
    7: CalendarDayData
  }
  employerData: SummaryEmployerData
}

/**
 * Interface representing daily mission summary for a specific weekday
 * @property durations - Worked durations for this weekday
 * @property costs.hourly - Hourly rate for this day
 * @property costs.hourlyInWorkedTime - Different hourly rates applied during work periods
 * @property costs.employerDataTotal - Total employer costs for this day
 * @property employerData - Employer-specific data for this day
 */
export interface SummaryDayData {
  durations: DayDurations
  costs: {
    hourly: number
    hourlyInWorkedTime: HourlyCostInTime[]
    employerDataTotal: number
  }
  employerData: SummaryEmployerData
}

/**
 * Interface representing strict calendar day durations
 * Durations are split by midnight boundaries (00h00-23h59 Paris time)
 * A work period from Saturday 22h to Sunday 05h is split:
 * - Saturday: 22h-00h (2h)
 * - Sunday: 00h-05h (5h)
 * IMPORTANT: Midnight is Paris time (Europe/Paris), not UTC
 * Used for employer rules with daysOfWeek restrictions
 * Note: Benefit durations are not tracked in calendarDays (only in days)
 */
export interface CalendarDayData {
  durations: {
    worked: {
      total: number
      day: number
      night: number
      timeRanges: DayTimeRange[]
      abandoned: number
      theoretical: number
    }
    total: {
      total: number
      day: number
      night: number
    }
    contract: {
      total: number
      day: number
      night: number
    }
  }
}

/**
 * Interface representing time ranges for a specific day
 * @property from - Start timestamp relative to day start (in ms from day start)
 * @property to - End timestamp relative to day start (in ms from day start)
 */
export interface DayTimeRange {
  from: number
  to: number
}

/**
 * Interface representing worked durations for a day of the week
 * @property worked.total - Total worked duration for the weekday (breaks subtracted)
 * @property worked.day - Total daytime worked duration for the weekday (breaks subtracted)
 * @property worked.night - Total nighttime worked duration for the weekday (breaks subtracted)
 * @property worked.timeRanges - Time ranges worked during the day
 * @property worked.theoretical - Theoretical duration to work for the day (breaks subtracted)
 * @property benefit - Benefit durations (e.g. paid leave)
 * @property total - Combined worked and benefit durations
 * @property theoretical - Theoretical duration to be on-site for the day (breaks included)
 * @property contract.total - Contractual duration for the day (from schedule if defined, otherwise equals theoretical)
 */
export type DayDurations = {
  worked: {
    total: number
    day: number
    night: number
    timeRanges: DayTimeRange[]
    abandoned: number
    theoretical: number
  }
  benefit: number
  total: {
    total: number
    day: number
    night: number
  }
  theoretical: number
  contract: {
    total: number
  }
}

/**
 * Interface representing hourly cost over a given period
 * This interface allows segmenting costs over different periods of the day
 * with potentially different hourly rates
 * @property value - Hourly rate in euros
 * @property from - Start timestamp of the period
 * @property to - End timestamp of the period
 * @property duration - Duration in milliseconds
 */
export interface HourlyCostInTime {
  value: number
  from: number
  to: number
  duration: number
}

/**
 * Interface representing data to increment or decrement a worker counter
 * @property ruleBasedIncrement - Potential increment (hours that would have gone to the counter)
 * @property increment - Actual increment (hours actually added to the counter)
 * @property decrement - Actual decrement (hours actually taken from the counter)
 * @property balanceChange - Net impact on the counter
 * @property balanceBefore - Counter balance before this week
 * @property balanceAfter - Counter balance after this week
 */
export interface PeriodSummaryCounterData {
  ruleBasedIncrement: number // heures qui auraient pu aller au compteur (calculé à partir des règles de calcul)
  allocation: {
    toCounter: number // heures ajoutées au compteur (par rapport aux règles de calcul et de l'ajustement de l'utilisateur)
    toPeriod: number // heures ajoutées au period summary (par rapport aux règles de calcul et de l'ajustement de l'utilisateur)
  }
  options: {
    adjustment: number // heures ajoutées au compteur (ajustement de l'utilisateur par rapport aux règles de calcul)
  }
  counter: {
    adjustedValue: number // impact net sur le compteur
    previousValue: number // solde compteur avant la création du period summary
    newValue: number // solde compteur après la création du period summary
    affectsPeriodSummaryAccounted: boolean // whether this counter affects the "accounted" duration in period summaries
  }
  workerCounterId: ResourceRef // id du compteur
  workerCounterTemplateId: ResourceRef // id du template de compteur
}

export interface SimulatedPeriodSummaryCounterData extends PeriodSummaryCounterData {
  workerCounter: WorkerCounterSchema
}

/**
 * Interface representing employer-specific data (from financial rules),
 * grouping duration calculation rules, objectives and costs
 * applied according to each employer's compensation policies
 * Note: Keys for durations, goals, costs are employer rule _id strings
 * Note: Keys for counters are worker counter template _id strings
 */
export interface SummaryEmployerData {
  durations: Record<string, number> // Keys are employer rule _id (as string)
  goals: Record<string, number> // Keys are employer rule _id (as string)
  costs: Record<string, number> // Keys are employer rule _id (as string)
  counters: Record<string, PeriodSummaryCounterData> // Keys are worker counter template _id (as string)
}
